import { inject } from '@angular/core';
import { CanActivateFn, ResolveFn, Route, Router, Routes } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { mvAuthGuard } from '../authentication/vb-lxp-auth.guard';
import { VbLxpAuthService } from '../authentication/vb-lxp-auth.service';
import { VbLxpLoginComponent } from '../authentication/vb-lxp-login.component';
import { VbLxpAdminComponent } from '../admin/vb-lxp-admin.component';
import { FeatureSettingsService } from './feature-settings.service';
import { IAppFeatures } from './IAppFeatures';
import { VbLxpVideoPopoutPageComponent } from '../video/video-popout-page/vb-lxp-video-popout-page.component';
import { WebSocketService } from './websocket.service';

export const featureSettingsResolver: ResolveFn<IAppFeatures> = () =>
	inject(FeatureSettingsService)
		.loadFeatureSettings()
		.pipe(
			catchError((error) => {
				console.error('Failed to load feature settings:', error);
				return throwError(() => error);
			}),
		);

	const logoutGuard: CanActivateFn = () => {
		const router = inject(Router);
		const authService = inject(VbLxpAuthService);
		const webSocketService = inject(WebSocketService);

		webSocketService.disconnect();
		authService.clearToken();
		localStorage.clear();
		sessionStorage.clear();

		return router.createUrlTree(['/login']);
	};

export function createAuthRoutes(options?: {
	loginTitle?: string;
	loginSubtitle?: string;
	loginPath?: string;
	redirectTo?: string;
	includeAdminRoute?: boolean;
	defaultUsername?: string;
	showStreamingEcsMenu?: boolean;
}): Routes {
	const {
		loginTitle = 'Admin Login',
		loginSubtitle = 'Sign in to access admin settings',
		loginPath = 'api/auth/login',
		redirectTo = '/admin',
		includeAdminRoute = true,
		defaultUsername,
		showStreamingEcsMenu = false,
	} = options || {};

	const routes: Routes = [
		{
			path: 'login',
			loadComponent: () => Promise.resolve(VbLxpLoginComponent),
			data: {
				title: loginTitle,
				subtitle: loginSubtitle,
				loginPath: loginPath,
				redirectTo: redirectTo,
				defaultUsername: defaultUsername,
			},
		},
		{
			path: 'admin/login',
			redirectTo: 'login',
			pathMatch: 'full',
		},
		{
			path: 'logout',
			canActivate: [logoutGuard],
			loadComponent: () => Promise.resolve(VbLxpLoginComponent),
		},
	];

	if (includeAdminRoute) {
		routes.push({
			path: 'admin',
			canActivate: [mvAuthGuard],
			data: {
				authRedirectTo: '/login',
				requireAdminRole: true,
				showStreamingEcsMenu,
			},
			loadComponent: () => Promise.resolve(VbLxpAdminComponent),
		});
	}

	return routes;
}

export function createVideoPopoutRoute(): Route {
	return {
		path: 'video-popout',
		loadComponent: () => Promise.resolve(VbLxpVideoPopoutPageComponent),
		data: {
			showHeader: false,
			showFooter: false,
			showLayoutControls: false,
			showTimingControl: false,
			showTimingTowerControl: false,
			showChatControl: false,
			showMapControl: false,
			showRaceClassControl: false,
		},
	};
}

/**
 * Wraps a route with authentication guard protection.
 * @param route The route configuration to protect
 * @param options Optional configuration for auth behavior
 * @returns The route with auth guard applied
 */
export function createProtectedRoute(
	route: Route,
	options?: {
		authRedirectTo?: string;
	},
): Route {
	const existingGuards = route.canActivate || [];

	return {
		...route,
		canActivate: [mvAuthGuard, ...existingGuards],
		data: {
			...route.data,
			authRedirectTo: options?.authRedirectTo || '/login',
		},
	};
}
